import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import walletService from '../services/walletService';
import toast from 'react-hot-toast';
import { 
  ArrowLeft,
  DollarSign,
  TrendingUp,
  CreditCard,
  Download,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  Wallet,
  X
} from 'lucide-react';

const LawyerWallet = () => {
  const navigate = useNavigate();
  const { language, user } = useApp();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const translations = {
    english: {
      wallet: 'Wallet',
      back: 'Back',
      balance: 'Available Balance',
      pendingBalance: 'Pending Balance',
      totalEarnings: 'Total Earnings',
      thisMonth: 'This Month',
      transactions: 'Recent Transactions',
      withdraw: 'Withdraw',
      addCard: 'Add Card',
      commission: 'Platform Commission',
      settlement: 'Settlement',
      pending: 'Pending',
      completed: 'Completed',
      noTransactions: 'No transactions yet',
      loading: 'Loading wallet...',
      withdrawFunds: 'Withdraw Funds',
      enterAmount: 'Enter amount to withdraw',
      insufficientBalance: 'Insufficient balance',
      withdrawSuccess: 'Withdrawal request submitted successfully',
      withdrawError: 'Failed to submit withdrawal request'
    },
    hindi: {
      wallet: 'बटुआ',
      back: 'वापस',
      balance: 'उपलब्ध शेष',
      pendingBalance: 'लंबित शेष',
      totalEarnings: 'कुल कमाई',
      thisMonth: 'इस महीने',
      transactions: 'हाल के लेन-देन',
      withdraw: 'निकासी',
      addCard: 'कार्ड जोड़ें',
      commission: 'प्लेटफॉर्म कमीशन',
      settlement: 'समझौता',
      pending: 'लंबित',
      completed: 'पूर्ण',
      noTransactions: 'अभी तक कोई लेन-देन नहीं',
      loading: 'बटुआ लोड हो रहा है...',
      withdrawFunds: 'धन निकालें',
      enterAmount: 'निकालने के लिए राशि दर्ज करें',
      insufficientBalance: 'अपर्याप्त शेष',
      withdrawSuccess: 'निकासी अनुरोध सफलतापूर्वक जमा किया गया',
      withdrawError: 'निकासी अनुरोध जमा करने में विफल'
    }
  };

  const t = translations[language] || translations.english;

  // Load wallet data
  useEffect(() => {
    if (!user?.uid) return;

    const loadWalletData = async () => {
      try {
        setLoading(true);
        
        // Load wallet data
        const walletResult = await walletService.getLawyerWallet(user.uid);
        if (walletResult.success) {
          setWalletData(walletResult.data);
        }

        // Load transactions
        const transactionsResult = await walletService.getWalletTransactions(user.uid, 20);
        if (transactionsResult.success) {
          setTransactions(transactionsResult.data);
        }
      } catch (error) {
        console.error('Error loading wallet data:', error);
        toast.error('Failed to load wallet data');
      } finally {
        setLoading(false);
      }
    };

    loadWalletData();

    // Subscribe to wallet updates
    const unsubscribe = walletService.subscribeToWalletUpdates(user.uid, (updatedWallet) => {
      if (updatedWallet) {
        setWalletData(updatedWallet);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // Handle withdrawal
  const handleWithdraw = async () => {
    if (!withdrawAmount || isNaN(withdrawAmount) || parseFloat(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (amount > walletData?.availableBalance) {
      toast.error(t.insufficientBalance);
      return;
    }

    setWithdrawLoading(true);
    try {
      const result = await walletService.withdrawFunds(user.uid, amount, {
        accountNumber: '1234567890',
        ifscCode: 'SBIN0001234',
        accountHolderName: user?.displayName || 'Lawyer'
      });

      if (result.success) {
        toast.success(t.withdrawSuccess);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
      } else {
        toast.error(result.error || t.withdrawError);
      }
    } catch (error) {
      console.error('Error withdrawing funds:', error);
      toast.error(t.withdrawError);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Get transaction icon and color
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'settlement':
        return { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-100' };
      case 'pending':
        return { icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-100' };
      case 'withdrawal':
        return { icon: Upload, color: 'text-red-500', bgColor: 'bg-red-100' };
      default:
        return { icon: Wallet, color: 'text-blue-500', bgColor: 'bg-blue-100' };
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/lawyer-dashboard')}
              className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-purple-500" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t.wallet}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage your earnings and payments
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Balance Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mb-6 space-y-4"
      >
        {loading ? (
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="animate-pulse">
              <div className="h-6 bg-purple-400 rounded mb-4"></div>
              <div className="h-8 bg-purple-400 rounded mb-2"></div>
              <div className="h-4 bg-purple-400 rounded"></div>
            </div>
          </div>
        ) : (
          <>
            {/* Available Balance */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t.balance}</h2>
            <DollarSign className="w-6 h-6" />
          </div>
              <div className="text-3xl font-bold mb-2">
                {formatAmount(walletData?.availableBalance)}
              </div>
          <div className="flex items-center gap-2 text-purple-100">
            <TrendingUp className="w-4 h-4" />
                <span className="text-sm">
                  +{formatAmount(walletData?.monthlyEarnings)} {t.thisMonth}
                </span>
              </div>
            </div>

            {/* Pending Balance */}
            {walletData?.pendingBalance > 0 && (
              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">{t.pendingBalance}</h2>
                  <Clock className="w-6 h-6" />
                </div>
                <div className="text-3xl font-bold mb-2">
                  {formatAmount(walletData?.pendingBalance)}
                </div>
                <div className="text-yellow-100 text-sm">
                  Will be available after consultation completion
                </div>
              </div>
            )}

            {/* Commission Info */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t.commission}
                </h2>
                <AlertCircle className="w-6 h-6 text-gray-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {formatAmount(walletData?.totalCommission)}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Total platform commission (20%)
          </div>
        </div>
          </>
        )}
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-2 gap-4 mb-6"
      >
        <button 
          onClick={() => setShowWithdrawModal(true)}
          disabled={!walletData?.availableBalance || walletData?.availableBalance <= 0}
          className="flex items-center justify-center gap-2 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-5 h-5 text-purple-500" />
          <span className="font-medium text-gray-900 dark:text-white">{t.withdraw}</span>
        </button>
        <button className="flex items-center justify-center gap-2 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-200">
          <CreditCard className="w-5 h-5 text-purple-500" />
          <span className="font-medium text-gray-900 dark:text-white">{t.addCard}</span>
        </button>
      </motion.div>

      {/* Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t.transactions}
        </h3>
        <div className="space-y-3">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))
          ) : transactions.length > 0 ? (
            transactions.map((transaction, index) => {
              const { icon: Icon, color, bgColor } = getTransactionIcon(transaction.type);
              const isCredit = transaction.type === 'settlement';
              const isDebit = transaction.type === 'withdrawal';
              
              return (
            <motion.div
              key={transaction.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bgColor}`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {transaction.description}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                          {transaction.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </p>
                        {transaction.type === 'pending' && (
                          <span className="inline-block mt-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            {t.pending}
                          </span>
                        )}
                        {transaction.type === 'settlement' && (
                          <span className="inline-block mt-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            {t.completed}
                          </span>
                        )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                        isCredit 
                      ? 'text-green-600 dark:text-green-400' 
                          : isDebit
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {isDebit ? '-' : '+'}{formatAmount(Math.abs(transaction.amount))}
                      </p>
                      {transaction.commission && (
                        <p className="text-xs text-gray-500">
                          Commission: {formatAmount(transaction.commission)}
                        </p>
                      )}
                </div>
              </div>
            </motion.div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t.noTransactions}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Your transaction history will appear here
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t.withdrawFunds}
              </h2>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.enterAmount}
                </label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Available: {formatAmount(walletData?.availableBalance)}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowWithdrawModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {withdrawLoading ? 'Processing...' : 'Withdraw'}
                </button>
              </div>
        </div>
      </motion.div>
        </div>
      )}
    </div>
  );
};

export default LawyerWallet;
